import { useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import {
  computeHeadroom,
  formatHeadroomDisplay,
  hasAnyLimit,
} from '../lib/limits';
import { useHeadroom } from '../hooks/useHeadroom';

/**
 * Persistent trader-side status strip showing live headroom against the
 * trader's caps. Mounted in TraderViewLayout so it shows across every trader
 * view (dashboard, positions, trades, etc).
 *
 * Source of truth is GET /api/trader/headroom, which is what the relay's
 * checkOrderLimit uses internally — keeps the widget honest about resting
 * intent + settled-ticker exclusions without making the client re-derive
 * the math from /positions and /pnl.
 *
 * Hides itself entirely when both caps are null so unlimited users don't
 * see clutter.
 */

const BAR_COLOURS: Record<string, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-red-500',
  over: 'bg-red-600',
  unlimited: 'bg-blue-400',
};

export function LimitsWidget() {
  const refreshMe = useAuthStore((s) => s.refreshMe);
  const head = useHeadroom();

  // Pull the latest caps into the authStore on mount so any consumer of
  // user.maxContracts / user.maxPositions sees fresh values right away.
  useEffect(() => {
    refreshMe().catch(() => {});
  }, [refreshMe]);

  if (!head) return null;
  const anyCap = hasAnyLimit({
    maxContracts: head.maxContracts,
    maxPositions: head.maxPositions,
  });
  const anyRule =
    head.rulesActive != null &&
    (head.rulesActive.sportRestricted ||
      head.rulesActive.nonSportsBlocked ||
      head.rulesActive.polymarketBlocked);
  if (!anyCap && !anyRule) {
    return null;
  }

  const contractsCurrent = head.openContracts + head.intentContracts;
  const cHead = computeHeadroom(contractsCurrent, head.maxContracts);
  const pHead = computeHeadroom(head.openPositions, head.maxPositions);
  const rulesSuffix = head.rulesActive
    ? [
        head.rulesActive.sportRestricted ? 'sport-restricted' : null,
        head.rulesActive.nonSportsBlocked ? 'no non-sport' : null,
        head.rulesActive.polymarketBlocked ? 'no new Poly' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className="border-border bg-muted/20 flex items-center gap-4 border-b px-4 py-1.5 text-[11px]">
      <span className="text-muted-foreground font-medium">Limits</span>
      {anyCap && (
        <>
          <BarRow
            label="Pos"
            current={head.openPositions}
            cap={head.maxPositions}
            status={pHead.status}
            fraction={pHead.fractionUsed}
          />
          <BarRow
            label="Cont"
            current={contractsCurrent}
            cap={head.maxContracts}
            status={cHead.status}
            fraction={cHead.fractionUsed}
          />
        </>
      )}
      {head.intentContracts > 0 && anyCap && (
        <span className="text-muted-foreground text-[10px]">
          (incl. {head.intentContracts.toLocaleString()} in resting buys)
        </span>
      )}
      {rulesSuffix && (
        <span className="text-amber-600 dark:text-amber-400 text-[10px]" title={rulesSuffix}>
          · {rulesSuffix}
        </span>
      )}
    </div>
  );
}

function BarRow({
  label,
  current,
  cap,
  status,
  fraction,
}: {
  label: string;
  current: number;
  cap: number | null;
  status: string;
  fraction: number | null;
}) {
  const widthPct = cap == null || fraction == null
    ? 100
    : Math.max(0, Math.min(100, fraction * 100));
  const bar = BAR_COLOURS[status] ?? 'bg-muted-foreground';
  return (
    <div className="flex flex-1 items-center gap-2">
      <span className="text-muted-foreground w-8 shrink-0">{label}</span>
      <div className="bg-muted/30 relative h-1.5 max-w-32 flex-1 overflow-hidden rounded">
        <div
          className={`${bar} h-full`}
          style={{ width: `${widthPct}%`, opacity: cap == null ? 0.35 : 1 }}
        />
      </div>
      <span className="text-foreground w-24 shrink-0 text-right tabular-nums">
        {formatHeadroomDisplay(current, cap)}
      </span>
    </div>
  );
}
